import { Input, InputRef, Radio } from 'antd';
import { Control, Controller, Path, RegisterOptions } from 'react-hook-form';
import HFbutton from '../../button/HFbutton';
import { forwardRef, useState } from 'react';
import { phoneFormat } from '../../../api/utils/format/numbers';
import { timeLeftCheck, timeLeftToString } from '../../../hooks/useTimer';
import useSmsHook from '../../../hooks/useSmsHook';
import BirthdayInput from './BirthdayInput';
import {
  COUNTRY_CODE_MAX_LENGTH,
  isKoreaCountryCode,
  normalizeCountryCode,
  normalizePhone,
} from '../../../constants/validation';
import { DISABLED_TEXT_CLASS } from './styles';

/**
 * react-hook-form 에 넘길 포커스 핸들을 만든다.
 * Radio.Group 처럼 ref 가 DOM 컨테이너인 입력은 컨테이너 자체가 포커스되지 않으므로,
 * 안쪽의 첫 입력 요소로 넘겨줘야 검증 실패 시 `shouldFocus` 가 동작한다.
 */
const createFocusHandle = (container: HTMLElement) => ({
  focus: () => {
    const target = container.querySelector<HTMLElement>(
      'input:not([disabled])',
    );
    // 포커스가 되면 브라우저가 알아서 화면 안으로 스크롤한다.
    if (target) {
      target.focus();
      return;
    }
    container.scrollIntoView?.({ block: 'center' });
  },
  select: () => {},
});

export type FormRowType =
  | 'text'
  | 'password'
  | 'text-button'
  | 'birthday'
  | 'gender'
  | 'phone'
  | 'phone-confirm'
  | 'country-code';
export interface FormRowProps<T> {
  type: FormRowType;
  name: Path<T>;
  control: Control<T>;
  className?: string;
  placeholder?: string;
  rules?: RegisterOptions;
  error?: boolean;
  disabled?: boolean;
  textButtonProp?: {
    text: string;
    action: () => void;
    disabled?: boolean;
  };
  autoComplete?: 'on' | 'off';
  /** 입력값 자체를 대문자로 변환한다. (css text-transform 은 표시만 바꾸므로 검증에 반영되지 않는다) */
  uppercase?: boolean;
  /**
   * `phone` 에서 함께 입력받은 국가 코드.
   * 국내 번호일 때만 하이픈을 붙여 보여주려고 쓴다. 넘기지 않으면 국내 번호로 본다.
   */
  countryCode?: string | null;
}

const FormRow = <T,>({
  type,
  name,
  rules,
  control,
  className,
  placeholder,
  error,
  disabled,
  textButtonProp,
  autoComplete = 'on',
  uppercase,
  countryCode,
}: FormRowProps<T>) => {
  switch (type) {
    case 'text':
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Input
              name={name}
              type="text"
              size="large"
              placeholder={placeholder}
              status={!error ? undefined : 'error'}
              value={field.value as string}
              onChange={(e) =>
                field.onChange(
                  uppercase ? e.target.value.toUpperCase() : e.target.value,
                )
              }
              ref={field.ref}
              className={`rounded p-3 ${disabled ? DISABLED_TEXT_CLASS : ''} ${className}`}
              disabled={disabled}
              autoComplete={autoComplete}
            />
          )}
        />
      );
    case 'password':
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Input.Password
              name={name}
              size="large"
              placeholder={placeholder}
              status={!error ? undefined : 'error'}
              value={field.value as string}
              onChange={(e) => field.onChange(e.target.value)}
              ref={field.ref}
              className={`rounded p-3 ${disabled ? DISABLED_TEXT_CLASS : ''} ${className}`}
              disabled={disabled}
              autoComplete={autoComplete}
            />
          )}
        />
      );
    case 'text-button':
      if (!textButtonProp) {
        throw new Error('textButton data is required');
      }
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <div className="relative flex w-full">
              <Input
                type="text"
                size="large"
                placeholder={placeholder}
                status={!error ? undefined : 'error'}
                value={field.value as string}
                onChange={(e) => field.onChange(e.target.value)}
                ref={field.ref}
                className={`rounded p-3 ${disabled ? DISABLED_TEXT_CLASS : ''} ${className}`}
                disabled={disabled}
              />
              <div className="!absolute right-2 top-1.5 rounded text-sm">
                <HFbutton
                  height={40}
                  onClick={() => textButtonProp.action()}
                  disabled={textButtonProp.disabled}
                >
                  {textButtonProp.text}
                </HFbutton>
              </div>
            </div>
          )}
        />
      );
    case 'birthday':
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <BirthdayInput
              ref={field.ref}
              value={field.value as string}
              onChange={field.onChange}
              disabled={disabled}
              error={error}
            />
          )}
        />
      );
    case 'gender':
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Radio.Group
              ref={(el) => {
                if (el) field.ref(createFocusHandle(el));
              }}
              size="large"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              className={`grid w-full grid-cols-2 ${
                disabled
                  ? '[&_.ant-radio-button-wrapper-disabled]:!text-gray7'
                  : ''
              }`}
              disabled={disabled}
              buttonStyle="solid"
            >
              <Radio.Button
                value="MALE"
                className={'text-center'}
                style={{
                  borderRadius: '0.25rem 0 0 0.25rem',
                  height: '50px',
                  lineHeight: '50px',
                  border: error ? '1px solid red' : '',
                }}
              >
                남
              </Radio.Button>
              <Radio.Button
                value="FEMALE"
                className={'text-center'}
                style={{
                  borderRadius: '0 0.25rem 0.25rem 0',
                  height: '50px',
                  lineHeight: '50px',
                  border: error ? '1px solid red' : '',
                }}
              >
                여
              </Radio.Button>
            </Radio.Group>
          )}
        />
      );
    case 'phone':
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <FormRowPhone
              ref={field.ref}
              placeholder={placeholder}
              status={!error ? undefined : 'error'}
              value={field.value as string}
              onChange={field.onChange}
              className={className}
              disabled={disabled}
              autoComplete={autoComplete}
              countryCode={countryCode}
            />
          )}
        />
      );
    case 'phone-confirm':
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <FormRowPhoneConfirm
              value={field.value as string}
              onChange={field.onChange}
              className={className}
            />
          )}
        />
      );
    case 'country-code':
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Input
              type="text"
              size="large"
              // '+' 는 입력할 필요 없이 항상 붙여 보여주고, 값은 숫자만 다룬다.
              prefix="+"
              placeholder={placeholder}
              status={!error ? undefined : 'error'}
              value={field.value as string}
              onChange={(e) =>
                field.onChange(normalizeCountryCode(e.target.value))
              }
              ref={field.ref}
              maxLength={COUNTRY_CODE_MAX_LENGTH}
              className={`rounded p-3 ${disabled ? DISABLED_TEXT_CLASS : ''} ${className}`}
              disabled={disabled}
              autoComplete={autoComplete}
            />
          )}
        />
      );
  }
};

const FormRowPhoneConfirm = ({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (phone: string) => void;
  className?: string;
}) => {
  const [phone, setPhone] = useState<string>(value);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isConfirm, setIsConfirm] = useState<boolean>(false);

  const { sendVerifyNumber, checkVerifyNumber, timeLeft } = useSmsHook();

  // 인증번호 전송
  const postSms = async () => {
    if (!phone) {
      alert('전화번호를 입력해주세요.');
      return;
    }
    sendVerifyNumber(phone).then((msg) => {
      alert(msg);
    });
  };

  // 인증코드 검증
  const putSms = async () => {
    if (!verificationCode) {
      alert('인증번호를 입력해주세요.');
      return;
    }
    checkVerifyNumber(phone, verificationCode).then((res) => {
      if (res.confirm) {
        setIsConfirm(true);
        onChange(phone);
      }
      alert(res.msg);
    });
  };

  // 전화번호 입력시 '-' 제거 및 인증 초기화
  const handlePhoneChange = (value: string) => {
    setPhone(value);
    setIsConfirm(false);
    setVerificationCode('');
  };

  return (
    <>
      <div className="relative flex w-full">
        <FormRowPhone
          placeholder="전화번호"
          value={phone}
          onChange={handlePhoneChange}
          disabled={isConfirm}
        />
        <div className="!absolute right-2 top-1.5 w-20 rounded text-sm">
          <HFbutton
            height={40}
            onClick={() => postSms()}
            disabled={isConfirm || !phone}
          >
            {timeLeftCheck(timeLeft) ? timeLeftToString(timeLeft) : '인증번호'}
          </HFbutton>
        </div>
      </div>
      <div className="relative w-full">
        <Input
          type="number"
          size="large"
          placeholder="인증번호"
          className={`rounded p-3 ${isConfirm ? DISABLED_TEXT_CLASS : ''} ${className}`}
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          disabled={isConfirm}
        />
        <div className="!absolute right-2 top-1.5 w-20 rounded text-sm">
          <HFbutton
            height={40}
            onClick={() => putSms()}
            disabled={isConfirm || !verificationCode}
          >
            {isConfirm ? '인증완료' : '인증확인'}
          </HFbutton>
        </div>
      </div>
    </>
  );
};

/** 검증 실패 시 포커스 이동이 되도록 ref를 Input까지 전달한다. */
const FormRowPhone = forwardRef<
  InputRef,
  {
    value: string;
    onChange: (phone: string) => void;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    status?: 'error' | undefined;
    autoComplete?: 'on' | 'off';
    countryCode?: string | null;
  }
>(
  (
    {
      value,
      onChange,
      className,
      placeholder,
      disabled,
      status,
      autoComplete,
      countryCode,
    },
    ref,
  ) => {
    const handlePhoneChange = (value: string) => {
      onChange(normalizePhone(value));
    };
    return (
      <Input
        ref={ref}
        type="text"
        size="large"
        placeholder={placeholder}
        className={`rounded p-3 ${disabled ? DISABLED_TEXT_CLASS : ''} ${className}`}
        // 하이픈은 국내 번호의 표기 방식이므로 해외 번호에는 붙이지 않는다.
        value={
          isKoreaCountryCode(countryCode) ? phoneFormat(value) : (value ?? '')
        }
        onChange={(e) => handlePhoneChange(e.target.value)}
        disabled={disabled}
        status={status}
        autoComplete={autoComplete}
      />
    );
  },
);

export default FormRow;
